import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import LogoCarousel from "@/components/new-landing/LogoCarousel";
import TrustBar from "@/components/TrustBar";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const diligenceItems = [
  {
    number: "01",
    title: "Product-Market Fit Validation",
    description:
      "Is the product solving a real problem for a large enough market? We validate fit with data, not assumptions.",
  },
  {
    number: "02",
    title: "Scalability and Technology Risk",
    description:
      "Identify scalability bottlenecks, technical liabilities, and infrastructure risks before they become yours.",
  },
  {
    number: "03",
    title: "Team Capability Assessment",
    description:
      "Evaluate product leadership and team strength against what the deal thesis actually requires.",
  },
  {
    number: "04",
    title: "Growth and Monetization Levers",
    description:
      "Map addressable revenue, pricing upside, and product-driven growth opportunities the current team may be missing.",
  },
  {
    number: "05",
    title: "AI Readiness and Execution Velocity",
    description:
      "Evaluate the target's AI capabilities, tooling maturity, and ability to execute at the speed modern markets demand.",
  },
];

const valueCreationItems = [
  {
    number: "01",
    title: "The 5% That Drives 95% of Growth",
    description:
      "Our core methodology. We audit your product and find the small set of changes that produce outsized business results. Stop spreading effort thin and focus where it matters.",
  },
  {
    number: "02",
    title: "Optimize Product for Revenue",
    description:
      "You don't always need to build new things. We make what you have drive more revenue through conversion optimization, pricing, and activation improvements.",
  },
  {
    number: "03",
    title: "Augment the Talent Gap",
    description:
      "We embed senior product leaders where your team falls short. No 6-month recruiting cycle. Impact in weeks.",
  },
  {
    number: "04",
    title: "Fix PMM to Unlock Revenue",
    description:
      "Align PMM with product reality. Convert product capabilities into revenue through better positioning, packaging, and go-to-market execution.",
  },
  {
    number: "05",
    title: "AI-Native Execution Velocity",
    description:
      "Compress timelines from quarters to weeks. Our team operates with AI-augmented tools that let us cover more ground and deliver results faster.",
  },
  {
    number: "06",
    title: "Board-Ready Metrics and Reporting",
    description:
      "Give PE firms and boards clear visibility into product-driven value creation. No more guessing whether product is contributing to the deal thesis.",
  },
];

const exitItems = [
  {
    number: "01",
    title: "The 5% That Drives 95% of Growth",
    description:
      "Our core methodology applied to pre-exit urgency. We audit your product and identify the small set of changes that will have the biggest impact on valuation metrics.",
  },
  {
    number: "02",
    title: "Revenue Optimization",
    description:
      "Make existing product drive more revenue. Conversion optimization, pricing, activation, expansion revenue. Quick wins that show up in the next board deck.",
  },
  {
    number: "03",
    title: "Augment the Talent Gap",
    description:
      "Embed senior product leaders where your team falls short. No time for a 6-month recruiting cycle before exit. We deliver impact in weeks.",
  },
  {
    number: "04",
    title: "Fix PMM for Revenue",
    description:
      "Align product marketing with product reality. Better positioning, packaging, and go-to-market that converts product capabilities into the revenue story buyers want to see.",
  },
  {
    number: "05",
    title: "AI-Native Execution",
    description:
      "Compress timelines from quarters to weeks. Our team operates with AI tools that let us cover more ground and deliver results faster than any traditional product org.",
  },
  {
    number: "06",
    title: "Build the Product Narrative",
    description:
      "Create the product story that increases multiples. Buyers pay premiums for clear vision, proven execution, and a defensible roadmap backed by evidence.",
  },
  {
    number: "07",
    title: "Board-Ready Metrics",
    description:
      "Give PE firms and boards clear visibility into product-driven value creation. Metrics that translate product work into financial language.",
  },
  {
    number: "08",
    title: "Exit-Ready Documentation",
    description:
      "Product capabilities, roadmap, team structure, and technical architecture documented for buyer due diligence. Answer diligence questions before they're asked.",
  },
];

const faqs = [
  {
    question: "What is product due diligence for private equity?",
    answer: "Product due diligence evaluates a target company's product-market fit, technology architecture, team capabilities, and growth potential before a PE firm closes a deal. It identifies product risks that financial diligence misses and validates whether the product can deliver on the deal thesis."
  },
  {
    question: "What does a PE operating partner do for product?",
    answer: "Operating partners typically oversee portfolio company performance, but most lack deep product expertise. We fill that gap by embedding senior product leaders who drive product-led value creation, EBITDA growth, and exit readiness across portfolio companies."
  },
  {
    question: "When should a PE firm bring in FractionalCPO?",
    answer: "Three key moments: before closing (product due diligence), during the first 100 days post-acquisition (value creation plan), and 12-24 months before exit (product story for buyers). The earlier FractionalCPO engages, the more value we can create."
  },
  {
    question: "Do you work with the portfolio company or through the fund?",
    answer: "Both. We can be engaged by the fund and embedded directly into the portfolio company. Operating partners get visibility into product performance while the portco team gets hands-on leadership."
  },
  {
    question: "How does product leadership impact PE exit multiples?",
    answer: "Product excellence directly drives revenue growth, user retention, and competitive defensibility. These are the metrics buyers evaluate. Companies with strong product leadership consistently achieve higher valuation multiples at exit."
  }
];

const PE = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="Product Leadership for Private Equity Firms"
        description="Fractional CPO services for PE firms. Product due diligence, post-investment value creation, and pre-exit preparation for portfolio companies."
        path="/pe"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "dateModified": "2026-03-24"
        }}
      />

      {/* Section 1: Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Private Equity
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-8"
          >
            Product leadership across
            <br />
            <span className="text-[#002178] font-medium">
              the deal lifecycle
            </span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mb-12"
          >
            <p className="text-lg text-black/60 leading-relaxed mb-4">
              A decade of proven playbooks for every stage of the PE deal lifecycle.{" "}
              Product is often the biggest lever PE firms underinvest in. 60% of
              value creation comes from revenue growth, yet most funds wait until
              Year 2 or 3 to address the product strategy driving it.
            </p>
            <p className="text-lg text-black/60 leading-relaxed">
              We help PE firms de-risk investments and accelerate value creation
              through product. From pre-deal diligence to exit preparation, we
              deliver the product leadership your portfolio companies need to hit
              their financial targets.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <button
              onClick={() => { trackEvent('contact_click', { source: 'hero' }); setIsContactOpen(true); }}
              className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform text-center"
            >
              Let's chat
            </button>
            <TrustBar />
          </motion.div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 md:py-16 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 mb-8">
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178]/50 font-medium text-center mb-8">
            Our team has led product at
          </p>
        </div>
        <LogoCarousel variant="light" />
        <div className="max-w-[900px] mx-auto px-6 md:px-12 mt-12">
          <motion.blockquote
            {...fadeUp}
            className="relative pl-12 md:pl-16"
          >
            <span className="absolute top-0 left-0 text-[#0047FF] text-[5rem] md:text-[6rem] font-serif leading-none select-none pointer-events-none">"</span>
            <p className="text-[clamp(1.125rem,2.5vw,1.5rem)] font-light leading-[1.5] mb-4 text-black/70 relative z-10">
              Love what you guys are doing since my restructure. Feedback is positive.
            </p>
            <cite className="text-black/40 text-sm not-italic font-medium">
              CEO, PE-Backed Platform
            </cite>
          </motion.blockquote>
        </div>
      </section>

      {/* Section 2: Pre-Investment Due Diligence */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
              >
                Before the Deal
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                Pre-Investment:
                <br />
                Product Due Diligence
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-black/60 leading-relaxed mb-8"
              >
                Product risk is the most overlooked risk in PE deals. Teams
                validate financials, legal, and market position. But few
                evaluate whether the product can actually deliver the growth
                the deal thesis requires. We close that gap before you close
                the deal.
              </motion.p>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <button
                  onClick={() => { trackEvent('contact_click', { source: 'diligence_section' }); setIsContactOpen(true); }}
                  className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
                >
                  Let's chat
                </button>
              </motion.div>
            </div>

            <div className="space-y-0">
              {diligenceItems.map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-black/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-[#002178]/30 font-mono text-sm">
                      {item.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 group-hover:text-[#002178] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-black/50 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Post-Investment Value Creation */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4 font-medium"
              >
                Value Creation
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
              >
                Post-Investment:
                <br />
                Value Creation
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/60 leading-relaxed mb-4"
              >
                A decade of proven playbooks for post-investment value creation. For operating partners who need product to deliver against the
                plan. We embed in the portfolio company and build the product
                strategy, team, and execution engine that hits financial targets.
              </motion.p>
              <motion.p {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }} className="text-white/60 leading-relaxed mb-8">
                <Link
                  to="/case-study"
                  className="text-white underline underline-offset-4 hover:text-white/80 transition-colors"
                >
                  See our case study &rarr;
                </Link>
              </motion.p>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <button
                  onClick={() => { trackEvent('contact_click', { source: 'value_creation_section' }); setIsContactOpen(true); }}
                  className="inline-block px-8 py-4 bg-white text-[#002178] font-medium hover:scale-105 transition-transform"
                >
                  Let's chat
                </button>
              </motion.div>
            </div>

            <div className="space-y-0">
              {valueCreationItems.map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-white/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-white/20 font-mono text-sm">
                      {item.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 text-white group-hover:text-white/80 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-white/50 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Pre-Exit Value Creation */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
              >
                Before the Exit
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                Pre-Exit:
                <br />
                Accelerating Value Creation
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-black/60 leading-relaxed mb-8"
              >
                This is where we deliver the most impact. A decade of proven
                playbooks for the 12-24 months before exit. We embed, execute,
                and drive the financial results that maximize your exit multiple.
              </motion.p>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <button
                  onClick={() => { trackEvent('contact_click', { source: 'exit_section' }); setIsContactOpen(true); }}
                  className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
                >
                  Let's chat
                </button>
              </motion.div>
            </div>

            <div className="space-y-0">
              {exitItems.map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-black/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-[#002178]/30 font-mono text-sm">
                      {item.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 group-hover:text-[#002178] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-black/50 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default PE;
